/**
 * AI archetype classifier (P547836103): the display gate and the body check for
 * validation_data.detailed_validations.ai_archetype.
 *
 * The MAP agent owns every value in the record. This module decides only
 * whether the stage has approved the record for display, and whether its body
 * has the types the panel renders. It never derives an archetype, a share, a
 * threshold result or an action.
 */

// window.APP_CONFIG keys written by the stage's runtime config. A missing,
// mistyped or empty value never matches, so the panel stays hidden.
const PANEL_ENABLED_KEY = 'AI_ARCHETYPE_PANEL_ENABLED';
const APPROVED_KEYS = {
  schema: 'AI_ARCHETYPE_APPROVED_SCHEMA_VERSION',
  producer: 'AI_ARCHETYPE_APPROVED_PRODUCER_DIGEST',
  classifier: 'AI_ARCHETYPE_APPROVED_CLASSIFIER_DIGEST',
  services: 'AI_ARCHETYPE_APPROVED_SERVICES_DIGEST',
};

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const isText = (value) => typeof value === 'string' && value !== '';
const approvedValue = (config, key) => (isText(config[key]) ? config[key] : null);
const matches = (value, approved) => approved !== null && value === approved;

/**
 * The plan-v28 section 7 predicate: panel flag on, evaluation mode visible, and
 * the record's schema and three config digests equal to the stage's approved
 * values. Read at render time so a rewritten config.js applies on reload.
 */
export const isAiArchetypeDisplayEligible = (record, appConfig = window.APP_CONFIG) => {
  const config = isObject(appConfig) ? appConfig : {};
  if (config[PANEL_ENABLED_KEY] !== true || !isObject(record)) return false;
  return record.evaluation_mode === 'visible'
    && matches(record.schema_version, approvedValue(config, APPROVED_KEYS.schema))
    && matches(record.producer?.config_digest, approvedValue(config, APPROVED_KEYS.producer))
    && matches(record.classifier?.config_digest, approvedValue(config, APPROVED_KEYS.classifier))
    && matches(record.services_list?.config_digest, approvedValue(config, APPROVED_KEYS.services));
};

export const ARCHETYPE_NAMES = {
  A: 'Workload migration + AI',
  B: 'AI platform migration',
  C: 'AI/ML modernization',
  D: 'Agentic build',
  E: 'Pure inference swap',
  NON_AI: 'Non-AI',
};
export const D_SUBTYPE_LABELS = {
  APT: 'APT',
  GREENFIELD: 'Greenfield',
  AMBIGUOUS: 'Ambiguous',
};
const CONFIDENCE_LABELS = { high: 'High', medium: 'Medium', low: 'Low' };
const FUNDING_RATIO_LABELS = { pass: 'Pass', fail: 'Fail', not_evaluated: 'Not evaluated' };
// not_applicable: no included Bedrock or AgentCore ARR (plan-v28 section 2).
const EVALUATION_STATUSES = ['complete', 'needs_review', 'not_applicable'];
const SHARE_KEYS = [
  'bedrock_share_basis_points',
  'ml_infra_share_basis_points',
  'gpu_share_basis_points',
  'dba_share_basis_points',
];

const isOptionalText = (value) => value === null || typeof value === 'string';
const isAbsentOrText = (value) => value === undefined || isOptionalText(value);
const isOptionalInteger = (value) => value === null || Number.isInteger(value);
const isTextList = (value) => Array.isArray(value) && value.every(isText);

const isSignal = (value) => isObject(value)
  && isText(value.rule_id)
  && isText(value.label)
  && isTextList(value.fields);
const isSignalList = (value) => Array.isArray(value) && value.every(isSignal);

const isArchetype = (value) => isObject(value)
  && Object.prototype.hasOwnProperty.call(ARCHETYPE_NAMES, value.code)
  && isText(value.name)
  && (value.d_subtype === null
    || (value.code === 'D' && Object.prototype.hasOwnProperty.call(D_SUBTYPE_LABELS, value.d_subtype)));

const isTreatment = (value) => isObject(value)
  && isText(value.code)
  && isText(value.label)
  && value.authoritative === false;

const isSource = (value) => isObject(value)
  && isOptionalText(value.source_type)
  && typeof value.source_data_gap === 'boolean'
  && isSignalList(value.evidence);

// A needs_review record whose producer block was unusable has no currency;
// it then carries no amounts either, since an amount needs its currency.
const isMetrics = (value) => isObject(value)
  && isText(value.basis)
  && isOptionalInteger(value.qualification_arr_cents)
  && isOptionalInteger(value.legacy_reconciliation_delta_cents)
  && (value.currency === null
    ? value.qualification_arr_cents === null && value.legacy_reconciliation_delta_cents === null
    : typeof value.currency === 'string' && /^[A-Z]{3}$/.test(value.currency))
  && SHARE_KEYS.every((key) => isOptionalInteger(value[key]));

const isFundingRatio = (value) => isObject(value)
  && Object.prototype.hasOwnProperty.call(FUNDING_RATIO_LABELS, value.status)
  && isOptionalInteger(value.share_basis_points)
  && isOptionalInteger(value.threshold_basis_points)
  && isOptionalText(value.effective_date);

// An axis the producer did not report has status null and renders as unknown.
const isAxis = (value) => value === null || (isObject(value) && (value.status === null || isText(value.status)));
const isLineTreatment = (value) => isObject(value)
  && isAbsentOrText(value.label)
  && isAbsentOrText(value.service_code)
  && isOptionalInteger(value.qualification_arr_cents)
  && isAxis(value.qualification)
  && isAxis(value.credit)
  && isAxis(value.ai_included_services);
const isLineTreatmentList = (value) => Array.isArray(value) && value.every(isLineTreatment);

const isCalculator = (value) => isObject(value)
  && isOptionalText(value.effective_calculator_id)
  && isOptionalText(value.source_calculator_id)
  && typeof value.repriced_calculator_used === 'boolean';

// Sections a needs_review record may leave out (null or absent). A complete
// record carries every one of them.
const OPTIONAL_WHEN_NEEDS_REVIEW = {
  archetype: isArchetype,
  confidence: (value) => Object.prototype.hasOwnProperty.call(CONFIDENCE_LABELS, value),
  map_treatment_recommendation: isTreatment,
  source: isSource,
  metrics: isMetrics,
  funding_ratio: isFundingRatio,
  deciding_signals: isSignalList,
  contradictions: isSignalList,
  line_treatments: isLineTreatmentList,
  suggested_reviewer_action: isText,
};

/**
 * Returns the record when its body has every type the panel renders, else
 * null. A null result renders "AI archetype recommendation unavailable" and
 * nothing from the record, so a partial body is never interpreted.
 */
export const readAiArchetypeBody = (record) => {
  if (!isObject(record)) return null;
  const identityOk = isText(record.evaluation_id)
    && isText(record.input_digest)
    && isText(record.evaluated_at)
    && EVALUATION_STATUSES.includes(record.evaluation_status)
    && record.advisory_only === true
    && isCalculator(record.calculator)
    && isText(record.producer?.schema_version)
    && isText(record.classifier?.ruleset_version)
    && isText(record.services_list?.policy_version)
    && isText(record.services_list?.source_version_id);
  if (!identityOk) return null;
  const complete = record.evaluation_status === 'complete';
  const sectionsOk = Object.entries(OPTIONAL_WHEN_NEEDS_REVIEW).every(([key, check]) => {
    const value = record[key];
    if (value === null || value === undefined) return !complete;
    return check(value);
  });
  return sectionsOk ? record : null;
};

export const confidenceLabel = (code) => CONFIDENCE_LABELS[code] || null;
export const fundingRatioLabel = (code) => FUNDING_RATIO_LABELS[code] || null;
